import { stableSerialize } from '../issues/facts';
import { sourceExcerpt } from './source-excerpt';
import type { IssueFactValue, IssueRawChange } from '../issues/types';
import { issueFieldLabel } from '../issues/fields';
import {
  CONTAINER_METADATA,
  describeFieldChange,
  fieldChangeMeaning,
} from '../issues/significance';
import type { NotificationBriefEntry } from './brief';
type Value = IssueFactValue | undefined;
const serialized = (value: Value) => stableSerialize(value);
/** Multiset difference preserves repeated rows and ignores pure collection reordering. */
export function changedCollection(before: IssueFactValue[], after: IssueFactValue[]) {
  const difference = (left: IssueFactValue[], right: IssueFactValue[]) => {
    const counts = new Map<string, number>();
    for (const row of right) {
      const key = serialized(row);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return left.filter((row) => {
      const key = serialized(row),
        count = counts.get(key) ?? 0;
      if (count) {
        counts.set(key, count - 1);
        return false;
      }
      return true;
    });
  };
  const beforeOnly = difference(before, after),
    afterOnly = difference(after, before);
  return {
    beforeOnly,
    afterOnly,
    reordered: !beforeOnly.length && !afterOnly.length && serialized(before) !== serialized(after),
  };
}
function changedRows(before: Value, after: Value) {
  return Array.isArray(before) || Array.isArray(after)
    ? changedCollection(Array.isArray(before) ? before : [], Array.isArray(after) ? after : [])
    : null;
}

function object(value: Value): value is Record<string, IssueFactValue> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
function hasCollection(value: Value): boolean {
  return Array.isArray(value) || (object(value) && Object.values(value).some(hasCollection));
}
function scalarContext(value: Value): Value {
  if (Array.isArray(value))
    return value.every((item) => item === null || typeof item !== 'object') && value.length <= 30
      ? value
      : undefined;
  if (!object(value)) return value;
  return Object.fromEntries(
    Object.entries(value).flatMap(([key, child]) => {
      const core = scalarContext(child);
      return core === undefined ? [] : [[key, core]];
    }),
  );
}
type RowGroup = { context: Value; rows: IssueFactValue[] };
type ProjectedChange = IssueRawChange & {
  beforeGroups?: RowGroup[];
  afterGroups?: RowGroup[];
  context?: Value;
  beforeContext?: Value;
  afterContext?: Value;
  contextOnly?: boolean;
};
/** Compare leaf collections inside structural containers. Context is evidence,
 * never a record identity or a claim that two array positions are the same entity. */
function projectedChanges(entry: NotificationBriefEntry) {
  const output: ProjectedChange[] = [];
  let omittedNestedPaths = 0;
  for (const change of entry.rawChanges) {
    if (!hasCollection(change.before) && !hasCollection(change.after)) {
      output.push(change);
      continue;
    }
    const sides = [
      new Map<string, { path: string; context: Value; rows: IssueFactValue[] }>(),
      new Map<string, { path: string; context: Value; rows: IssueFactValue[] }>(),
    ];
    const collect = (
      value: Value,
      path: string,
      context: IssueFactValue[],
      side: number,
      depth = 0,
    ) => {
      if (depth > 12) {
        omittedNestedPaths++;
        return;
      }
      const add = (rows: IssueFactValue[], suffix = '') => {
        const key = serialized([path + suffix, context]);
        const item = sides[side]!.get(key) ?? { path: path + suffix, context, rows: [] };
        item.rows.push(...rows);
        sides[side]!.set(key, item);
      };
      if (Array.isArray(value)) {
        const nested = value.some((row) => object(row) && Object.values(row).some(hasCollection));
        if (!nested) {
          add(value);
          return;
        }
        const cores = value.map((row) => scalarContext(row) ?? null);
        add(cores, '[fields]');
        for (const row of value)
          if (object(row))
            for (const [key, child] of Object.entries(row))
              if (hasCollection(child))
                collect(
                  child,
                  `${path}[].${key}`,
                  [...context, scalarContext(row) ?? null],
                  side,
                  depth + 1,
                );
      } else if (object(value)) {
        add([scalarContext(value) ?? null], '[fields]');
        for (const [key, child] of Object.entries(value))
          if (hasCollection(child)) collect(child, `${path}.${key}`, context, side, depth + 1);
      } else if (value !== undefined) add([value]);
    };
    collect(change.before, change.path, [], 0);
    collect(change.after, change.path, [], 1);
    const paths = new Set(
      [...sides[0]!.values(), ...sides[1]!.values()].map((group) => group.path),
    );
    for (const path of paths) {
      const beforeGroups = [...sides[0]!.values()].filter((group) => group.path === path),
        afterGroups = [...sides[1]!.values()].filter((group) => group.path === path);
      const before = beforeGroups.flatMap((group) => group.rows),
        after = afterGroups.flatMap((group) => group.rows);
      const delta = changedCollection(before, after);
      const associations = (groups: typeof beforeGroups) =>
        groups
          .map((group) => serialized([group.context ?? null, group.rows.map(serialized).sort()]))
          .sort();
      const contextChanged =
        serialized(associations(beforeGroups)) !== serialized(associations(afterGroups));
      const rowChanged = delta.beforeOnly.length > 0 || delta.afterOnly.length > 0;
      if (!rowChanged && !delta.reordered && !contextChanged) continue;
      const contexts = (groups: typeof beforeGroups) =>
        groups.map((group) => ({ context: group.context ?? null, rowCount: group.rows.length }));
      output.push({
        path,
        ...(afterGroups.length === 1 ? { context: afterGroups[0]!.context } : {}),
        beforeContext: contexts(beforeGroups),
        afterContext: contexts(afterGroups),
        contextOnly: !rowChanged && contextChanged,
        beforeGroups,
        afterGroups,
        ...(beforeGroups.length ? { before } : {}),
        ...(afterGroups.length ? { after } : {}),
      });
    }
  }
  // Accepted after-leaf rows precede metadata and old-context history. No entity
  // pairing is inferred when scalar/header context changes.
  output.sort(
    (a, b) =>
      Number(b.after !== undefined) - Number(a.after !== undefined) ||
      Number(a.contextOnly) - Number(b.contextOnly) ||
      Number(a.path.endsWith('[fields]')) - Number(b.path.endsWith('[fields]')) ||
      b.path.split('.').length - a.path.split('.').length,
  );
  return { changes: output, omittedNestedPaths };
}

/** A correction button needs a positive accepted outcome, not inferred urgency,
 * an unverified repair, a comment instruction or an unchanged historical row.
 * Unrecognized source vocabularies keep the neutral review action. */
export function hasNotificationCorrection(entry: NotificationBriefEntry) {
  if (entry.issueIdentity === null) return false;
  if (
    entry.after?.sourceState === 'not_listed' ||
    (typeof entry.after?.status === 'string' &&
      /^(?:closed|passed)$/i.test(entry.after.status.trim()))
  )
    return false;
  const correction = (value: Value) =>
    typeof value === 'string' && /^corrections? (?:required|needed)$/i.test(value.trim());
  const outcomeField = (key: string) => /^(?:status|result|outcome)$/i.test(key.trim());
  const outcome = (value: Value) =>
    object(value) &&
    Object.entries(value).some(([key, value]) => outcomeField(key) && correction(value));
  if (outcome(entry.after)) return true;
  return projectedChanges(entry).changes.some((change) => {
    const delta = changedRows(change.before, change.after);
    if (!delta) return false;
    return delta.afterOnly.some((row) => {
      if (object(row)) return outcome(row);
      if (!Array.isArray(row)) return false;
      const candidates = rowContexts(row, change.afterGroups);
      return (
        candidates.length > 0 &&
        candidates.every(({ context }) => {
          const table = Array.isArray(context) ? context.at(-1) : null;
          const headers = object(table) && Array.isArray(table.headers) ? table.headers : [];
          return headers.some(
            (header, index) =>
              typeof header === 'string' && outcomeField(header) && correction(row[index]),
          );
        })
      );
    });
  });
}

function segment(value: Value, budget: number) {
  if (value === undefined) return { value: null, present: false, omitted: false };
  if (JSON.stringify(value).length <= budget) return { value, present: true, omitted: false };
  function trim(item: Value, limit: number, depth = 0): Value {
    if (typeof item === 'string')
      return item.length > limit ? `${item.slice(0, limit)} [text omitted]` : item;
    if (!item || typeof item !== 'object') return item;
    if (depth > 5) return '[nested facts omitted]';
    if (Array.isArray(item))
      return item.slice(0, 8).map((child) => trim(child, limit, depth + 1) ?? null);
    return Object.fromEntries(
      Object.entries(item)
        .slice(0, 30)
        .map(([key, child]) => [key, trim(child, limit, depth + 1) ?? null]),
    );
  }
  for (const limit of [500, 180, 60]) {
    const result = trim(value, limit);
    if (JSON.stringify(result).length <= budget)
      return { value: result, present: true, omitted: true };
  }
  return { value: null, present: true, omitted: true };
}
/** Preserve rare changed scalar values before repeated rows, without assigning domain meaning.
 * Interleave ends on ties; ordering of a supplied collection is not chronology. */
export function representativeRows(values: IssueFactValue[]) {
  const frequencies = new Map<string, Map<string, number>>();
  for (const row of values) {
    if (!row || typeof row !== 'object') continue;
    for (const [key, value] of Object.entries(row)) {
      if (value && typeof value === 'object') continue;
      const field = frequencies.get(key) ?? new Map<string, number>();
      const id = serialized(value);
      field.set(id, (field.get(id) ?? 0) + 1);
      frequencies.set(key, field);
    }
  }
  const score = (row: IssueFactValue) => {
    if (!row || typeof row !== 'object') return 0;
    return Object.entries(row).reduce((total, [key, value]) => {
      const field = frequencies.get(key);
      return (
        total +
        (field && field.size > 1 && field.size < values.length
          ? 1 / (field.get(serialized(value)) ?? 1)
          : 0)
      );
    }, 0);
  };
  const order: IssueFactValue[] = [];
  for (let i = 0; i < Math.ceil(values.length / 2); i++) {
    order.push(values[i]!);
    if (values.length - 1 - i !== i) order.push(values[values.length - 1 - i]!);
  }
  return order
    .map((row, index) => ({ row, index, score: score(row) }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((item) => item.row);
}
function rowContexts(value: IssueFactValue, groups: RowGroup[] = []) {
  const key = serialized(value);
  return groups.flatMap((group) => {
    const occurrences = group.rows.filter((row) => serialized(row) === key).length;
    return occurrences ? [{ context: group.context ?? null, occurrences }] : [];
  });
}

function rows(values: IssueFactValue[], budget: number, groups: RowGroup[] = []) {
  const selected: ReturnType<typeof segment>[] = [];
  let remaining = budget;
  for (const value of representativeRows(values)) {
    const candidates = rowContexts(value, groups);
    const row = {
      ...segment(value, Math.min(1500, remaining - 80)),
      contexts: segment(candidates, 450),
      contextAmbiguous: candidates.length > 1,
    };
    const size = JSON.stringify(row).length;
    if (remaining < size + 80) break;
    selected.push(row);
    remaining -= size;
  }
  return { rows: selected, totalRows: values.length, omittedRows: values.length - selected.length };
}
function background(value: Value, depth = 0): { value: Value; omitted: boolean } {
  if (Array.isArray(value))
    return {
      value: {
        omittedBackgroundRows: value.length,
        reason: 'Historical collection; changed rows are supplied separately',
      },
      omitted: value.length > 0,
    };
  if (value && typeof value === 'object') {
    if (depth >= 6) return { value: '[nested background omitted]', omitted: true };
    let omitted = Object.keys(value).length > 30;
    const pairs = Object.entries(value)
      .slice(0, 30)
      .map(([key, child]) => {
        const result = background(child, depth + 1);
        omitted ||= result.omitted;
        return [key, result.value ?? null];
      });
    return { value: Object.fromEntries(pairs), omitted };
  }
  return { value, omitted: false };
}
function snapshot(value: Value, budget: number) {
  const projected = background(value);
  const bounded = segment(projected.value, budget);
  return { ...bounded, omitted: projected.omitted || bounded.omitted };
}
export function notificationEvidence(entry: NotificationBriefEntry) {
  const changes = [];
  let remaining = 5500;
  const projected = projectedChanges(entry);
  for (const change of projected.changes) {
    const collection = changedRows(change.before, change.after);
    const value = change.contextOnly
      ? {
          path: change.path,
          kind: 'context_association_changed',
          rowContentMultisetUnchanged: true,
          beforeAssociations: rows(
            Array.isArray(change.before) ? change.before : [],
            1400,
            change.beforeGroups,
          ),
          afterAssociations: rows(
            Array.isArray(change.after) ? change.after : [],
            1400,
            change.afterGroups,
          ),
        }
      : collection
        ? (() => {
            const edited = collection.reordered ? null : containerFieldChanges(change);
            // One page or table with edited details: the writer sees only the details
            // that differ, with plain labels and meanings. The unchanged rest of the
            // page is deliberately absent, so it cannot be repeated as news. A page
            // whose title, headers or links also changed keeps its full rows as context.
            if (edited?.length && containerMetadataUnchanged(change)) {
              const fieldChanges = segment(
                edited.slice(0, 20).map(({ field, before, after }) => {
                  const { label, meaning } = fieldChangeMeaning(field, {
                    source: entry.source,
                    recordKind: entry.recordKind,
                  });
                  const row: Record<string, IssueFactValue> = { label, before, after };
                  if (meaning) row.meaning = meaning;
                  return row;
                }),
                Math.min(3000, Math.max(900, remaining - 300)),
              );
              // Many edits at once: only when the details fit does the page's rest stay out.
              if (fieldChanges.value !== null)
                return {
                  path: change.path,
                  context: segment(change.context, 450),
                  kind: 'container_fields_changed',
                  fieldChanges,
                  unchangedDetailsOmitted: true,
                };
            }
            return {
              path: change.path,
              context: segment(change.context, 450),
              kind: collection.reordered
                ? 'reordered_collection'
                : change.path.endsWith('[fields]')
                  ? 'container_fields_changed'
                  : 'changed_collection',
              beforeCount: Array.isArray(change.before) ? change.before.length : null,
              afterCount: Array.isArray(change.after) ? change.after.length : null,
              beforeValue: Array.isArray(change.before) ? null : segment(change.before, 300),
              afterValue: Array.isArray(change.after) ? null : segment(change.after, 300),
              beforeOnly: rows(collection.beforeOnly, 1800, change.beforeGroups),
              afterOnly: rows(collection.afterOnly, 1800, change.afterGroups),
            };
          })()
        : {
            path: change.path,
            kind: 'changed_value',
            before: segment(change.before, 1300),
            after: segment(change.after, 1300),
          };
    const size = JSON.stringify(value).length;
    if (size > remaining) break;
    changes.push(value);
    remaining -= size;
  }
  return {
    updateCategory: entry.issueIdentity === null ? 'source_coverage' : 'record',
    trigger: {
      changes,
      omittedChangePaths: projected.changes.length - changes.length,
      omittedNestedPaths: projected.omittedNestedPaths,
    },
    before: snapshot(entry.before, 900),
    after: snapshot(entry.after, 2200),
    transitions: segment(entry.transitions, 500),
  };
}
/** Group-less page/table containers keep their source fields under this key. */
const FIELD_GROUP = 'fields';

/** Flatten a scalar context into labelled pairs without ever printing JSON. */
export function flattenFacts(
  value: Value,
  prefix = '',
  depth = 0,
  options: { metadata?: 'top' | 'none' } = {},
): Array<{ path: string; value: string }> {
  if (depth > 6 || value === undefined) return [];
  if (Array.isArray(value)) {
    const scalars = value.filter((item) => item === null || typeof item !== 'object');
    if (scalars.length === value.length)
      return value.length ? [{ path: prefix, value: scalars.map(String).join(', ') }] : [];
    return value.flatMap((item, index) =>
      flattenFacts(item, `${prefix}[${index + 1}]`, depth + 1, options),
    );
  }
  if (object(value))
    return Object.entries(value).flatMap(([key, child]) => {
      // Container titles, headers and links describe the page or table, never the record.
      if ((!prefix || options.metadata === 'none') && CONTAINER_METADATA.has(key)) return [];
      // Page fields are the record; the grouping key adds nothing a reader needs.
      const path = key === FIELD_GROUP && !prefix ? '' : prefix ? `${prefix}.${key}` : key;
      return flattenFacts(child, path, depth + 1, options);
    });
  return [{ path: prefix, value: value === null ? 'Not recorded' : String(value) }];
}

/** The source context a row sat under, as plain labelled facts (unit, address, section). */
function contextLabel(context: Value) {
  return flattenFacts(context, '', 0, { metadata: 'none' })
    .map((item) => `${fieldLabel(item.path.replace(/^(?:\[\d+\]\.?)+/, ''))}: ${item.value}`)
    .join('; ');
}

export type FieldChange = { field: string; before: string | null; after: string | null };
/**
 * When a container changed on exactly one side each, the pair is the same page
 * or table with edited details. Name only the details that differ; everything
 * else in the container is unchanged history.
 */
export function containerFieldChanges(change: ProjectedChange): FieldChange[] | null {
  if (!change.path.endsWith('[fields]') || change.contextOnly) return null;
  const delta = changedRows(change.before, change.after);
  if (!delta || delta.beforeOnly.length !== 1 || delta.afterOnly.length !== 1) return null;
  const before = new Map(flattenFacts(delta.beforeOnly[0]).map((item) => [item.path, item.value]));
  const after = new Map(flattenFacts(delta.afterOnly[0]).map((item) => [item.path, item.value]));
  const fields = [...new Set([...before.keys(), ...after.keys()])];
  return fields
    .filter((field) => before.get(field) !== after.get(field))
    .map((field) => ({
      field,
      before: before.get(field) ?? null,
      after: after.get(field) ?? null,
    }));
}

/** The two containers of a `[fields]` edit share their title, headers and links. */
export function containerMetadataUnchanged(change: ProjectedChange) {
  const delta = changedRows(change.before, change.after);
  if (!delta || delta.beforeOnly.length !== 1 || delta.afterOnly.length !== 1) return false;
  const metadata = (value: IssueFactValue) =>
    serialized(
      object(value)
        ? Object.fromEntries([...CONTAINER_METADATA].map((key) => [key, value[key] ?? null]))
        : null,
    );
  return metadata(delta.beforeOnly[0]!) === metadata(delta.afterOnly[0]!);
}

export const OMISSION_NOTE = 'Some changed details are omitted here; the full record is in AddAva';

function fieldLabel(path: string) {
  return issueFieldLabel(path.replace(/\[\d+\]$/, ''));
}

/** Source-grounded changed rows for the deterministic fallback; never infer a new city issue. */
export function changedRowSummaries(entry: NotificationBriefEntry) {
  const projected = projectedChanges(entry).changes;
  const leaf = projected.filter(
    (change) => !change.path.endsWith('[fields]') && !change.contextOnly,
  );
  const fields = projected.filter(
    (change) => change.path.endsWith('[fields]') && !change.contextOnly,
  );
  // Edited page details are always named; a changed top-level value never hides them.
  const selected = leaf.length || fields.length ? [...leaf, ...fields] : projected;
  let omitted = false;
  const clip = (text: string, max: number) => {
    const excerpt = sourceExcerpt(text);
    if (excerpt.omitted) {
      omitted = true;
      return excerpt.text;
    }
    if (text.length <= max) return text;
    omitted = true;
    return text.slice(0, max) + '…';
  };
  const descriptions = selected.flatMap((change) => {
    // The same page or table with edited details: say which details changed.
    const edited = containerFieldChanges(change);
    if (edited) {
      if (!edited.length) return [];
      const shown = edited.slice(0, 4);
      if (edited.length > shown.length) omitted = true;
      return [
        shown
          .map(({ field, before, after }) =>
            describeFieldChange(field, before, after, clip, {
              source: entry.source,
              recordKind: entry.recordKind,
            }).replace(/\.$/, ''),
          )
          .join('. '),
      ];
    }
    const changed = change.contextOnly
      ? {
          beforeOnly: Array.isArray(change.before) ? change.before : [],
          afterOnly: Array.isArray(change.after) ? change.after : [],
          reordered: false,
        }
      : changedRows(change.before, change.after);
    if (!changed) return [];
    if (changed.reordered) return ['The source reordered these entries without changing them.'];
    const describe = (row: IssueFactValue, side: 'before' | 'after') => {
      const candidates = rowContexts(
        row,
        side === 'after' ? change.afterGroups : change.beforeGroups,
      );
      const labels = candidates.map((candidate) => contextLabel(candidate.context)).filter(Boolean);
      const contextPrefix =
        change.path.endsWith('[fields]') || !labels.length
          ? ''
          : candidates.length > 1
            ? `Under one of ${clip(labels.join(' or '), 110)} (association uncertain): `
            : `Under ${clip(labels[0]!, 110)}: `;
      const withContext = (text: string) => contextPrefix + text;
      if (Array.isArray(row)) {
        // Ambiguous candidates still share their table headers when every candidate agrees.
        const headerSets = (candidates.length ? candidates.map((c) => c.context) : [change.context])
          .map((contexts) => (Array.isArray(contexts) ? contexts.at(-1) : null))
          .map((context) =>
            object(context) && Array.isArray(context.headers) ? context.headers : [],
          );
        const headers =
          headerSets.length &&
          headerSets.every((set) => serialized(set) === serialized(headerSets[0]!))
            ? headerSets[0]!
            : [];
        if (row.length > 6) omitted = true;
        return withContext(
          row
            .slice(0, 6)
            .map(
              (value, index) =>
                `${clip(String(headers[index] ?? `column ${index + 1}`), 35)}: ${clip(
                  typeof value === 'string'
                    ? value
                    : flattenFacts(value)
                        .map((item) => item.value)
                        .join(', '),
                  70,
                )}`,
            )
            .join('; '),
        );
      }
      if (!row || typeof row !== 'object') return withContext(clip(String(row), 130));
      const pairs = flattenFacts(row).filter(({ path }) => !['id', 'key'].includes(path));
      // Interleave fields from both ends so metadata prefixes cannot systematically
      // hide trailing source details. This order does not assign domain meaning.
      const interleaved: typeof pairs = [];
      for (let i = 0; i < Math.ceil(pairs.length / 2); i++) {
        interleaved.push(pairs[pairs.length - 1 - i]!);
        if (pairs.length - 1 - i !== i) interleaved.push(pairs[i]!);
      }
      if (pairs.length > 6) omitted = true;
      return withContext(
        interleaved
          .slice(0, 6)
          .map(({ path, value }) => `${clip(fieldLabel(path), 35)}: ${clip(value, 70)}`)
          .join('; '),
      );
    };
    const after = representativeRows(changed.afterOnly).slice(0, 2),
      before = representativeRows(changed.beforeOnly).slice(0, 1);
    const details = [
      ...after.map(
        (row) =>
          `${change.path.endsWith('[fields]') ? 'Now shows' : change.contextOnly ? 'Now listed as' : 'Now listed'}: ${describe(row, 'after')}`,
      ),
      ...before.map(
        (row) =>
          `${change.path.endsWith('[fields]') ? 'Previously showed' : 'Previously listed'}: ${describe(row, 'before')}`,
      ),
    ];
    if (changed.afterOnly.length - after.length + changed.beforeOnly.length - before.length > 0)
      omitted = true;
    if (details.join('. ').length > 300) omitted = true;
    return details;
  });
  // Context-only echoes of a selected edit are not omitted information.
  const leftOut = projected.some((change) => !change.contextOnly && !selected.includes(change));
  // The note precedes facts, so the final brief clipping cannot remove it.
  return [...(omitted || leftOut ? [OMISSION_NOTE] : []), ...descriptions];
}
